import { useRef } from 'react';
import { Typography } from '@mui/material';
import type { TypographyProps } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { useValueFlash } from '../hooks/useValueFlash';
import { VALUE_FLASH_UP_SX, VALUE_FLASH_DOWN_SX } from '../styles/realtimeAnimations';

interface AnimatedValueProps {
    value: number;
    format: (v: number) => string;
    /** Only flash when this reference changes (e.g. RTDB data). Prevents
     *  false flashes on user-driven changes like date range or account switches. */
    trigger?: unknown;
    variant?: TypographyProps['variant'];
    fontWeight?: string;
    sx?: SxProps<Theme>;
    align?: TypographyProps['align'];
}

/**
 * Renders a formatted number that flashes green on increase or red on decrease.
 * Self-contained: each instance tracks its own previous value internally.
 */
export const AnimatedValue = ({ value, format, trigger, variant, fontWeight, sx, align }: AnimatedValueProps) => {
    const flash = useValueFlash(value, trigger);
    const flashCountRef = useRef(0);
    const wasFlashingRef = useRef(false);

    // Render-phase ref mutation: the key must be computed synchronously so React
    // can diff it in the same render pass. This means Strict Mode (dev only) may
    // double-increment the counter, but since it only controls CSS animation replay
    // (not data), a skipped key number is harmless — the animation still plays once.
    if (flash && !wasFlashingRef.current) {
        flashCountRef.current += 1;
    }
    wasFlashingRef.current = !!flash;

    const flashSx = flash === 'up'
        ? VALUE_FLASH_UP_SX
        : flash === 'down'
            ? VALUE_FLASH_DOWN_SX
            : {};

    // Use a stable key that only changes when a new flash is triggered.
    // The animation plays via CSS `forwards` fill mode — no remount needed when it ends.
    return (
        <Typography
            key={`v-${flashCountRef.current}`}
            variant={variant}
            fontWeight={fontWeight}
            align={align}
            sx={{ ...((sx || {}) as object), ...((flashSx || {}) as object) }}
        >
            {format(value)}
        </Typography>
    );
};
